import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Vue } from 'vue-property-decorator';
import BaseObjectTypeEnum from '@/enums/BaseObjectTypeEnum';
import ListItem from '@/types/fields/ListItem';
import Dictionary from '@/types/Dictionary';
import axios from 'axios';
import { API_URL, MAIN_FILTER_TREE } from '@/App.settings';
import Feature from '@/types/Feature';
import Bookmark from '@/types/Bookmark';
import WorkingModeEnum from '@/enums/WorkingModeEnum';
import { i18n } from '@/main';
import EnumItem from '@/types/fields/EnumItem';
import UserItem from '@/types/UserItem';

/** 
 * Store global contenant des actions utilisées dans tous les composants Vue sans préfixe du namespace
 * Il contient essentiellement le SingleNode et des informations de la fonction en cours d'utilisation
 */
@Module({
    namespaced: false
})
class GlobalStore extends VuexModule {
//#region Data
    //#region Gestion du masque de chargement
    private loadingMaskVisible: boolean = false;
    private loadingMaskMessage: string = "";
    //#endregion Gestion du masque de chargement

    //#region Gestion du mode de travail
    /** Mode de travail (tableau de bord principal par défaut)
     * @todo A remplacer par une machine d'état !!!!
     */
    private workingMode: WorkingModeEnum = WorkingModeEnum.MainDashboard;

    /** 
     * Mode de travail précédent
     * @todo A remplacer par une machine d'état !!!!
     *  */
    private previousMode: WorkingModeEnum = WorkingModeEnum.MainDashboard;
    //#endregion

    //#region Gestion des fonctions

    //#region Liste des fonctions
    /** Liste des fonctions du menu */
    private menu: Feature[] = [];
    /** Liste des bookmarks du menu des fonctions*/
    private bookmarks: Bookmark[] = [];
    /** Liste des fonctions de Master */
    private masters: Feature[] = [];
    //#endregion Liste des fonctions

    //#region Fonction courante
    /** Fonction courante */
    private currentFeature: Feature = undefined;
    //#endregion Fonction courante

    //#region Fonction courante par défaut
    /** @todo: A voir avec SLavina. Nécessite de récupérer la liste des features.vue dès le démarrage (actuellement dans Features.vue qui est chargé lors de l'accès au menu) */
    private defaultFeaturePath: string = "HeritageManagement / Consultation / Display";                             // "Gestion Patrimoniale / Consultation / Affichage";
    //#endregion Fonction courante par défaut

    //#region Fonction contenant la fiche objet
    private readonly objectDisplayingFeaturePath: string  = "HeritageManagement / Consultation / Display";          // "Gestion Patrimoniale / Consultation / Affichage";
    //#endregion Fonction contenant la fiche objet

    /** Indique si les évènements d'une fonction doit être désactivé
     *  @hack : Utilisé pour l'interopérabilité avec DCx V2. Les events Ext.net chargés dans l'Iframe de IFrameLoaderFiltered.vue interfère avec le déplacement du splitpanes de Workbench.vue
     **/
    private spanResizing: boolean = false;
    //#endregion Gestion des fonctions

    /** Dictionnaire de listes à charger en asynchrone dans les différents formulaires objet */
    private listItemsObject: Dictionary<ListItem[]> = {};

    /** Dictionnaire des enumeration à charger */
    private listEnumsValues: Dictionary<EnumItem[]> = {};

    /** Liste des utlisateurs */
    private usersList: UserItem[] = []; 

    /** Dictionnaire de liste filtrée à partir d'un périmètre donné */
    private listPerimeterItems: Dictionary<any> = {};

//#endregion Data
//#region Mutations
    /** Réinitialisation du store VueX */
    @Mutation
    private reset() {
        this.loadingMaskVisible = false;
		this.loadingMaskMessage = ""; // "Veuillez patienter...";
        this.workingMode = WorkingModeEnum.MainDashboard;
        this.previousMode = WorkingModeEnum.MainDashboard;
        this.menu = [];
        this.masters = [];

        this.currentFeature = undefined;

        this.spanResizing = false;
        this.listItemsObject = {};
        this.listEnumsValues = {};
        this.usersList = [];
        this.listPerimeterItems = {};
    }

    /** 
     * Affichage du masque de chargement prenant la totalité de la page
     * @param message Message à afficher dans le masque
     * @see control-loadingmask.vue
     */
	@Mutation
	private showLoadingMask(message: string) {
		this.loadingMaskVisible = true;
		this.loadingMaskMessage = message;
	}

    /** 
     * Masquage du masquage de chargement prenant la totalité de la page
     * @see control-loadingmask.vue
     */
	@Mutation
	private hideLoadingMask() {
		this.loadingMaskVisible = false;
		this.loadingMaskMessage = "";
	}

    /** Mise-à-jour du mode de travail courant
     * @param workingMode : mode de travail
     */
    @Mutation
    private setWorkingMode(workingMode: WorkingModeEnum) {
        this.workingMode = workingMode;
    }

    /** Mise-à-jour du mode de travail précédent
     * @param workingMode : mode de travail
     */
    @Mutation
    private setPreviousMode(workingMode: WorkingModeEnum) {
        this.previousMode = workingMode;
    }

    /** 
     * Défini la fonction courante
     * @param feature       Fonction qui va être défini comme fonction courant
     **/
    @Mutation
    private setFeature(feature) {
        this.currentFeature = feature;
    }

    /** Défini le menu de fonctions */
    @Mutation
    private setFeaturesMenu(features: Feature[]) {
        this.menu = features;
    }
    /** Défini le menu des bookmarks */
    @Mutation
    private setBookmarksMenu(bookmarks: Bookmark[]){
        this.bookmarks = bookmarks;
    }

    /** Défini les fonctions du Master */
    @Mutation
    private setFeaturesMaster(features: Feature[]) {
        this.masters = features;
    }

    /**
     * Récupération des la liste des utilisateurs
     * @param users liste des utilisateurs à charger
     */
    @Mutation
    private setUserItems(users: UserItem[]){
        this.usersList = [];

        for (let user of users) {
            this.usersList.push(new UserItem( user.Id, user.FullName, user.IsManager));
        }
    }

    /**
     * Chargement d'une liste
     * @param targetDic Nom de la variable contenant le dictionnaire à mettre à jour
     * @param listName Nom de la clé dans le dictionnaire
     * @param listItems
     */
    @Mutation
    private setListItems({ targetDic, listName, listItems }) {
        console.debug("setListItems - listName : " + listName + " - listItems : %O", listItems);

        // HACK: nécessité d'utiliser Vue.set et non "this.listItemsObject[listName] = listItems;". get GetListItems() n'est alors pas réactif sans l'usage de evolution sur le dictionnaire
        Vue.set(this[targetDic], listName, listItems);
    }

    @Mutation
    private setSpanResizing(value: boolean) {
        this.spanResizing = value;
    }

//#endregion Mutations
//#region Actions
    /** Réinitialisation du store VueX */
    @Action({ rawError: true })
    public Reset() {
        this.context.commit('reset');
    }

    /** 
     * Affichage du masque de chargement prenant la totalité de la page
     * @param message Message à afficher dans le masque
     * @see control-loadingmask.vue
     */
	@Action({ rawError: true })
	public ShowLoadingMask(message: string) {
		this.context.commit('showLoadingMask', message);
	}

    /** 
     * Masquage du masquage de chargement prenant la totalité de la page
     * @see control-loadingmask.vue
     */
	@Action({ rawError: true })
	public HideLoadingMask() {
		this.context.commit('hideLoadingMask');
	}

    //#region Gestion du mode de travail
    /** 
     * Bascule entre les modes "Environnement de travail (Workbench) et tableau de bord général (MainDashboard)" et le mode Administration
     * @todo Crado ! Implémenter une state machine pour gérer cela plus proprement !!!!
     */
    @Action({ rawError: true })
    public ToggleMode(workingMode: WorkingModeEnum) {
        console.debug("GlobalStore - ToggleMode() - workingMode : %O", workingMode);
        if (this.workingMode === workingMode) {
            this.context.commit('setWorkingMode', this.previousMode);
            /** Réactive la map */
            if (this.workingMode == WorkingModeEnum.Map)
                this.context.dispatch('CartoMod/SetIsVisible', true, { root: true });
        }
        else {
            /** Vérifie si l'utilisateur à les droits sur le workingMode demandé */
            if (this.context.getters['GetMasterAction'](WorkingModeEnum[workingMode].toString())) {
                /** Désactive la map si on affiche un autre mode ==> @todo crado. Implémenter une state machine ! */
                if (this.workingMode == WorkingModeEnum.Map)
                    this.context.dispatch('CartoMod/SetIsVisible', false, { root: true });

                this.context.commit('setPreviousMode', this.workingMode);
                this.context.commit('setWorkingMode', workingMode);
            }
            else {
                throw new Error("L'utilisateur courant n'a pas les droits sur la fonction demandée '" + WorkingModeEnum[workingMode].toString() + "' !" );
            }
        }
    }
    //#endregion Gestion du mode de travail

    /** Défini la fonction en cours d'utilisation
     * @param path Chemin de la fonction à sélectionner
     */
    @Action({ rawError: true })
    public async SetFeature(path: string) {
        let foundFeature = searchFeature('Path', path, this.menu);
        if (foundFeature) {
            this.context.commit("setFeature", foundFeature);
            console.debug('GlobalStore - SetFeature() - path :', path);
            await this.context.dispatch(MAIN_FILTER_TREE + "/ChangeSelectModeStrategy", { selectMode : foundFeature.SelectMode }, { root: true });
        }
    }

    /** Pour ouverture de la fiche objet 
    * @todo : à supprimer à terme quand les features seront gérés dans le GlobalStore 
    **/
    @Action({ rawError: true })
    public async SetObjectDisplayingFeature() {
        // Recherche de la fonction à partir de son chemin
        let feature: Feature = this.GetFeature(this.objectDisplayingFeaturePath);
        if (feature) {
            this.context.commit('setFeature', feature);
            console.debug('GlobalStore - SetObjectDisplayingFeature');
            // await this.context.dispatch(MAIN_FILTER_TREE + "/ChangeSelectModeStrategy", { selectMode : feature.SelectMode }, { root: true });
        }
    }

    @Action({ rawError: true })
    private async SetDefaultFeature() {
        let feature: Feature = this.GetFeature(this.defaultFeaturePath);
        if (feature) {
            this.context.commit("setFeature", feature);
            console.debug('GlobalStore - SetDefaultFeature');
            await this.context.dispatch(MAIN_FILTER_TREE + "/ChangeSelectModeStrategy", { selectMode : feature.SelectMode }, { root: true });
        }
    }

    /** Chargement du menu des fonctions et bookmarks */
    @Action({ rawError: true })
    public async LoadFeaturesMenu(){
        let data = {
            token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : ""
        };

        await axios.post(API_URL + "navigation/getFeaturesMenu", data).then((response) => {
            if (response && response.data && response.data['features'] && response.data['bookmarks']) 
            {
                let boookmarks : Bookmark[]= response.data['bookmarks'];
                let features : Feature[] =  response.data['features'];

                // Fonction récursive d'initialisation de la propriété "IsBookmark" sur l'arbre des fonctionnalités.
                let initIsBookmark = (feature : Feature) =>
                {
                    if(boookmarks.find(b => b.Feature == feature.Path)){
                        feature.IsBookmark = true;
                    }
                    else{
                        feature.IsBookmark = false;
                    }
                    if(feature.Features && feature.Features.length > 0)
                    {
                        feature.Features.forEach(subFeature => {
                            initIsBookmark(subFeature);
                        });
                    }
                }
                // Initialisation des propriétés "IsBookmark" de l'arbre des fonctionnalités.
                features.forEach(feature => {
                    initIsBookmark(feature);
                });

                this.context.commit('setFeaturesMenu', features);
                this.context.commit('setBookmarksMenu', boookmarks);

                // Réinitialise la fonction par défaut
                this.context.dispatch('SetDefaultFeature');
            }
        });
    }

    /** Chargement des fonctions du master */
    @Action({ rawError: true })
    public async LoadFeaturesMaster() {
        let data = {
            token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : ""
        };

        await axios.post<Feature[]>(API_URL + "navigation/GetFeaturesMaster", data).then((response) => {
            if (response && response.data) {
                this.context.commit('setFeaturesMaster', response.data);
            }
        })
    }

    /** 
     * Chargement de listes de façon asynchrone
     * @param listName: Nom de la liste asynchrone à charger
     * @param targetFilter : Nom du filtre à appliquer
     * @param filterValue : Valeur à appliquer
     */
    @Action({ rawError: true })
    public async LoadListItems(listItems: { listName, targetFilter, filterValue }[]) {
        let propListItems: { listName, targetFilter, filterValue }[] = [];
        for (let listItem of listItems) {
            if (!this.listItemsObject[listItem.listName + listItem.targetFilter + listItem.filterValue]) {
                console.debug("LoadListItems - listName : " + listItem.listName + " - targetFilter : " + listItem.targetFilter + " - filterValue : " + listItem.filterValue);
                if (listItem.targetFilter == undefined && listItem.filterValue == undefined)
                    propListItems.push({ listName: listItem.listName, targetFilter: "", filterValue: 0 });
                else
                    propListItems.push(listItem);
            }
        }

        if (propListItems.length > 0) {
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["PropListItems"] = propListItems;

            await axios.post<Dictionary<ListItem[]>>(API_URL + "base/LoadListItems", args).then((response) => {
                if (response && response.data) {
                    console.debug("LoadListItems - Success - listName : ", response.data);
                    let dicListItems: Dictionary<ListItem[]> = response.data;
                    for (let propListItem of propListItems) {
                        console.debug("LoadListItems - success - key : %O", propListItem.listName + propListItem.targetFilter + propListItem.filterValue);
                        this.context.commit('setListItems', {
                            targetDic: "listItemsObject", listName: propListItem.listName + propListItem.targetFilter + propListItem.filterValue
                            , listItems: dicListItems[propListItem.listName + propListItem.targetFilter + propListItem.filterValue]
                        });
                    }
                }
            });
        }
    }

    /** 
     * Chargement d'une liste filtrée à partir d'un périmètre
     * @param listName: Nom de la liste à charger
     */
    @Action({ rawError: true })
    public async LoadPerimeterItems({ listName }) {
        /** Si la liste n'est pas déjà chargée */
        if (!this.listPerimeterItems[listName]) {
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["ListName"] = listName;

            await axios.post<Dictionary<any>>(API_URL + "base/LoadPerimeterItems", args).then((response) => {
                if (response && response.data) {
                    console.debug("LoadPerimeterItems - Success - listName : ", listName);
                    console.debug("LoadPerimeterItems - Success - response.data : ", response.data);

                    let data: Dictionary<any> = response.data;

                    let splitList: string[] = listName.split(',');

                    for (var i = 0; i < splitList.length; i++) {
                        this.context.commit('setListItems', { targetDic: "listPerimeterItems", listName: splitList[i], listItems: data[splitList[i]] });
                    }
                }
            });
        }
    }

    /** 
     * Chargement d'enumeration de façon asynchrone
     * @param enumName: Nom de l'enumeration à charger
     * @param featureName : Nom de la feature à appeler
     */
    @Action({ rawError: true })
    public async LoadListEnums(listEnums: { featureName: string, enumNames: string[] }) {
        let propListEnums: { featureName: string, enumNames: string[] };

        console.debug("LoadListEnums - Test : ", listEnums);

        if (listEnums && 'featureName' in listEnums && 'enumNames' in listEnums) {
            for (let enumName of listEnums.enumNames) {
                let fullEnumName: string = enumName + (listEnums.featureName ? '|' + listEnums.featureName : "");
                if (!this.listEnumsValues[fullEnumName]) {
                    console.debug("LoadListEnums - Success - fullEnumName : " + fullEnumName);
                    if (!propListEnums)
                        propListEnums = { featureName: listEnums.featureName, enumNames: [] };
                    propListEnums.enumNames.push(enumName);
                }
            }
        }

        if (propListEnums) {
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["EnumNames"] = propListEnums.enumNames;

            await axios.post<Dictionary<ListItem[]>>(API_URL + (propListEnums.featureName ? "Feature/" + propListEnums.featureName : "base") + "/LoadListEnums", args).then((response) => {
                if (response && response.data) {
                    console.debug("LoadListEnums - Success axios - enumName : ", propListEnums.enumNames);
                    console.debug("LoadListEnums - Success axios - response.data : " + response.data);
                    let dicListEnums: Dictionary<ListItem[]> = response.data;
                    for (let listEnum in dicListEnums) {
                        this.context.commit('setListItems', { targetDic: "listEnumsValues", listName: listEnum + (listEnums.featureName ? '|' + listEnums.featureName : ""), listItems: dicListEnums[listEnum] });
                    }
                }
            });
        }
    }

    /** 
     * Indique si un redimenssionnement est du splitspan en cours dans Workbench.vue.
     * ==> Utilisé pour informer IFrameLoaderFiltered afin de désactiver les events Ext.net dans l'IFrame qui intérfère avec les events de vue.js
     */
    @Action({ rawError: true })
    public SetSpanResizing(value: boolean) {
        this.context.commit('setSpanResizing', value);
    }


    /** Permet le chargement des utilisateurs */
    @Action({rawError: true})
    public LoadUsers(){
        const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            // Interroge l'API correspondant au informations utilisateurs
            axios.post(API_URL + "User/LoadUsers", args).then((response) => {
                this.context.commit('setUserItems', response.data);
            });
    }

//#endregion Actions
//#region Getters
	/** Indique si le masque de chargement est affiché ou non  */
	public get GetShowLoadingMask() {
		return this.loadingMaskVisible;
	}

	/** Renvoi le message à afficher lors du chargement  */
	public get GetLoadingMaskMessage() {
		return this.loadingMaskMessage;
	}

    /** Obtient le mode de travail courant */
    public get GetWorkingMode(): WorkingModeEnum {
        return this.workingMode;
    }

    /** retourne la liste des utilisateurs */
    public get GetUsers(): UserItem[] {
        return this.usersList;
    }

    /** Retourne le nom de la fonction courante */
    public get GetFeatureName(): string {
        return this.currentFeature?.Name;
    }

    /** Chemin de la fonction courante sur DCx V2 */
    public get GetFeatureInterop(): string {
        return this.currentFeature?.Interop;
    }

    /** Chemin de la fonction courante */
    public get GetFeaturePath(): string {
        return this.currentFeature?.Path;
    }

    /** Chemin de la fonction courante avec les libellés parent */
    public get GetFeaturePathLabels(): string {
        let pathLabels: string = "";
        if (this.currentFeature) {
            let pathArray = this.currentFeature.Path.split(" / ");

            console.debug("GlobalStore - GetFeaturePathLabels() - pathArray", pathArray);

            // Chemin complet de la fonction avec le label de la fonction parente
            for (let i = 0; i < pathArray.length; i++) {
                console.debug("GlobalStore - GetFeaturePathLabels - for storeI18n.t(pathArray[i]).toString() : '%s'", i18n.t(pathArray[i]).toString());
                pathLabels += '/ ' + i18n.t(pathArray[i]).toString() + " ";
            };
            /** Retire le 1er caractère '/' */
            if (pathLabels && pathLabels.length > 2)
                pathLabels = pathLabels.substring(2);
        }

        return pathLabels;
    }

    /** Récupère le path initial du menu des fonctionnalités */
    public get GetDefaultFeaturePath(): string {
        return this.defaultFeaturePath;
    }

    /** Composant Vue cible de la fonction courante */
    public get GetFeatureTarget(): string {
        return this.currentFeature?.Target;
    }

    /** Liste des fonctions du menu */
    public get GetFeaturesMenu(): Feature[] {
        return this.menu;
    }
    /** Liste des bookmarks du menu */
    public get GetBookmarksMenu() : Bookmark[]{
        return this.bookmarks;
    }

    /** Retourne une liste de features filtrés à partir de noms de Features passé en paramètre
     * @param featurePaths                  Chemin de features à filtrer
     * @param featureNamesOrder             Tableau de nom des fonctions pour ordonner les fonctions
     * @returns                             Tableau de Features
     */
    public get GetFilteredFeatures() {
        return (featurePaths: string[], featureNamesOrder: string[]): Feature[] => {
            let features: Feature[] = [];
            searchFeatures('Path', featurePaths, this.menu, features);

            if (featureNamesOrder && featureNamesOrder.length > 0) {
                features.sort((a, b) => {
                        return featureNamesOrder.indexOf(a.Name) - featureNamesOrder.indexOf(b.Name);
                    }
                )
            }

            return features;
        };
    }

    /** Obtient une fonction à partir de son nom
     * @param featurePath   Nom de la fonction à obtenir
     * @returns Feature
     */
    public get GetFeature() {
        return (featurePath: string): Feature => {
            return getFeature(featurePath, this.menu);
        }
    }

    /** Liste des fonctions accessibles depuis Master */
    public get GetFeaturesMaster(): Feature[] {
        return this.masters;
    }

    /** 
     * Obtient les droits associés à une fonction
     * @param featurePath Chemin de la fonction recherchée
     * @returns Retourne une chaine contenant les droits associés à une fonction.
     * @returns Retourne undefined si la fonction recherchée n'a pas été trouvée.
     *  */
    public get CheckActionMenu() {
        return (featurePath: string[]): string | undefined => {
            let feature: Feature = searchFeature('Path', featurePath.join(" / "), this.menu);
            return feature.Action;
        }
    }

    /** 
     * Obtient les droits associés à une fonction
     * @param featureName Nom de la fonction recherchée
     * @returns Retourne une chaine contenant les droits associés à une fonction.
     * @returns Retourne undefined si la fonction recherchée n'a pas été trouvée.
     *  */
    public get GetMasterAction() {
        return (featureName: string): string | undefined => {
            let feature: Feature = this.masters.find(x => x.Name == featureName);
            if (feature)
                return feature.Action;
            else
                return undefined;
        }
    }

    /** 
     * Vérifie si un droit est un inclus dans les droits d'une fonction pour l'utilisateur courant
     * @param featureName       Nom de la fonction concernée
     * @param checkedAction     Action à vérifier
     * @param featureAction     Action associée à une feature à comparer avec checkedAction
     * @returns Retourne true ou false selon que le droit est inclus ou non. Retourne undefined si la fonction n'a pas été trouvée
     *  */
    public get CheckAction() {
        return (checkedAction: string, featureName: string = undefined, featureAction: string = undefined): boolean => {
            let actionLevel: Dictionary<string[]> = {};

            /**  
             * Liste les droits inclus dans chaque niveau de droits : chaque niveau de droit inclus les droits du précédent.
             * @todo A améliorer parce qu'un peu pourri de faire comme ça quand même...🙄
             */
            actionLevel['Read'] =   ['Read'];
            actionLevel['Write'] =  [...actionLevel['Read'],    'Write' ];
            actionLevel['Add'] =    [...actionLevel['Write'],   'Add'   ];
            actionLevel['Delete'] = [...actionLevel['Add'],     'Delete'];

            let action: string = featureAction ?? this.context.getters['GetMasterAction'](featureName);

            console.debug("GlobalStore - CheckAction() - featureName : %s - action : %s - masterAction : %s - actionLevel : %O",
                featureName, checkedAction, action, actionLevel, this.masters);

            if (action)
                return actionLevel[action].includes(checkedAction);
            else {
                if (featureName != undefined && featureName.includes('Supervision/'))
                    return true;
                return undefined;
            }
        };
    }

    /** Retourne le nom de la fonction utilisée comme fonction 'Affichage' */
    public get GetObjectDisplayingFeaturePath(): string {
        return this.objectDisplayingFeaturePath;
    }

    /** 
     * Renvoi une liste d'item à charger à partir d'un nom de liste asynchrone
     * @param listName : Nom de la liste asynchrone à charger
     * @param targetFilter : Nom du filtre à appliquer
     * @param filterValue : Valuer à appliquer
     * @param excludedItems Ids d'items de la liste à exclure
     **/
    public get GetListItems() {
        return (listName: string, targetFilter: string = "", filterValue: number = 0, excludedItems: number[] = undefined): ListItem[] => {
            console.debug('GetListItems - listName : ' + listName + " - targetFilter : " + targetFilter + " - filterValue : " + filterValue);

            let listItems: ListItem[] = [];

            if ((listName + targetFilter + filterValue) in this.listItemsObject)
                listItems = this.listItemsObject[listName + targetFilter + filterValue];

            if (excludedItems) {
                console.debug("GlobalStore - GetListItems() - excludedItems : %O - listItems : %O - listItems.filter(x => !(excludedItems.includes(x.Id))) : %O", excludedItems, listItems, listItems.filter(x => !(excludedItems.includes(x.Id))));
                listItems = listItems.filter(x => !(excludedItems.includes(x.Id)));
            }

            return listItems;
        }
    }

    /** 
     * Renvoi une liste d'enumeration à charger à partir d'un nom d'énumeration
     * @param enumName : Nom de la liste asynchrone à charger
     **/
    public get GetListEnums() {
        return (enumName: string): EnumItem[] => {
            console.debug('GetListEnums - enumName : ' + enumName);

            let listEnums: EnumItem[] = [];

            if ((enumName) in this.listEnumsValues)
                listEnums = this.listEnumsValues[enumName];

            return listEnums;
        }
    }


    /**
     * Renvoi une liste d'items à partir d'un nom de liste filtrée sur un périmètre
     * @param listName Nom de la liste
     **/
    public get GetPerimeterItems() {
        return (listName: string): any[] => {
            let listItems: any[] = [];

            if (this.listPerimeterItems && listName in this.listPerimeterItems)
                listItems = this.listPerimeterItems[listName];

            return listItems;
        }
    }

    /** 
     * Indique si un redimenssionnement est du splitspan en cours dans Workbench.vue.
     * ==> Utilisé pour informer IFrameLoaderFiltered afin de désactiver les events Ext.net dans l'IFrame qui intérfère avec les events de vue.js
     */
    public get GetSpanResizing(): boolean {
        return this.spanResizing;
    }
//#endregion Getters
};

//#region Fonctions locales
/** 
 * Recherche d'une fonction dans la liste des fonctions
 * @param property  Nom de la propriété dans laquelle rechercher pour la fonction
 * @param value     Valeur de la propriété à rechercher pour la fonction
 * @param features  Liste des fonctions dans laquelle rechercher
 * @returns         Retourne la fonction trouvée.
 */
function searchFeature(property: string, value: string, features: Feature[]): Feature {
    for (let feature of features) {
        if (feature[property] === value)
            return feature;
        else
            if (feature.Features) {
                let foundFeature = searchFeature(property, value, feature.Features);
                if (foundFeature)
                    return foundFeature;
            }
    }
    return;
}

/** 
 * Recherche d'une fonction dans la liste des fonctions
 * @param property  Nom de la propriété dans laquelle rechercher pour la fonction
 * @param values    Valeurs de la propriété à rechercher pour la fonction sous forme de regex
 * @param rootPath  Indique la racine du chemin de la fonction à charger
 * @param features  Liste des fonctions dans laquelle rechercher
 * @returns         Retourne les fonctions trouvées.
 */
function searchFeatures(property: string, values: string[], features: Feature[], foundedFeatures: Feature[]): void {
    for (let feature of features) {
        /** Recherche la fonction */
        let index: number = values.findIndex(function(pattern) {
            return new RegExp(pattern).test(feature[property]);
        })
        
        if (index > -1)
            foundedFeatures.push(feature);
        else
            if (feature.Features)
                searchFeatures(property, values, feature.Features, foundedFeatures);
    }
}

/** 
 * Obtient un objet Feature à partir d'un chemin contenu dans menu
 * @param featurePath Chemin de la fonction à obtenir
 * @param menu        Liste des fonctions dans laquelle rechercher
 * @returns           Retourne la fonction trouvée.
 * */
function getFeature(featurePath: string, menu): Feature {
    let features: Feature[] = [];
    searchFeatures('Path', [featurePath], menu, features);
    return features ? features[0] : undefined;
}
//#endregion Fonctions locales

export default GlobalStore;